import React, { useState } from 'react';
import { Clock, Share2, Check } from 'lucide-react';
import ShareModal from '../ui/ShareModal';
import { API_BASE_URL } from '../../config';

function XIcon({ size = 14, className = '' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.742l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.91-5.622Zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function cleanSourceUrl(url) {
  if (!url) return url;
  // Strip trailing " (Twitter)" or "%20(Twitter)" that sometimes gets appended to usernames
  return url.replace(/%20\([^)]*\)/g, '').replace(/ \([^)]*\)/g, '');
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).toUpperCase();
}

export default function BestCallCard({ call }) {
  const returnPct = parseFloat(call.total_return_pct ?? call.return_pct ?? call.current_return_pct ?? 0);
  const returnColor = returnPct > 0 ? 'text-emerald-600' : returnPct < 0 ? 'text-red-600' : 'text-gray-500';

  const directionLabel = call.direction
    ? call.direction.charAt(0).toUpperCase() + call.direction.slice(1)
    : null;

  const confidenceLabel = call.confidence
    ? call.confidence.charAt(0).toUpperCase() + call.confidence.slice(1) + ' Confidence'
    : null;

  const directionStyle =
    call.direction === 'bullish'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : call.direction === 'bearish'
      ? 'bg-red-50 text-red-700 border-red-200'
      : 'bg-gray-50 text-gray-600 border-gray-200';

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const predictionShareUrl =
    call.id != null
      ? `${window.location.origin}/analysts/predictions/${call.id}`
      : null;

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden relative">
      {/* Rank corner badge */}
      {call.rank != null && (
        <div className="absolute top-0 right-0 w-14 h-14 overflow-hidden pointer-events-none">
          <div
            className="absolute top-0 right-0"
            style={{
              width: 0,
              height: 0,
              borderTop: '56px solid #9a8c2c',
              borderLeft: '56px solid transparent',
            }}
          />
          <span className="absolute top-1.5 right-1.5 text-white text-xs font-bold leading-none">
            #{call.rank}
          </span>
        </div>
      )}

      {/* Header: symbol info + return */}
      <div className="flex items-start justify-between px-4 pt-4 pb-3">
        <div className="flex items-center gap-3">
          {/* Symbol logo box */}
          <div className="w-11 h-11 rounded-lg bg-blue-50 flex items-center justify-center font-bold text-blue-700 text-xs flex-shrink-0 overflow-hidden">
            {call.symbol_logo_url
              ? <img src={call.symbol_logo_url} alt={call.symbol_ticker} className="w-full h-full object-contain p-1" />
              : (call.symbol_ticker ?? '').slice(0, 4)}
          </div>
          <div>
            <div className="font-bold text-gray-900 text-base leading-tight">
              {call.symbol_name ?? call.company_name ?? call.symbol_ticker}
            </div>
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              {directionLabel && (
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded border uppercase tracking-wide ${directionStyle}`}>
                  {directionLabel}
                </span>
              )}
              {confidenceLabel && (
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded border uppercase tracking-wide bg-emerald-50 text-emerald-700 border-emerald-200">
                  {confidenceLabel}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Return */}
        <div className="text-right pr-10 flex-shrink-0">
          <div className={`text-2xl font-bold ${returnPct === 0 ? 'text-gray-500' : returnColor}`}>
            {returnPct === 0 ? 'N/A' : `${returnPct >= 0 ? '+' : ''}${returnPct.toFixed(1)}%`}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-gray-400 mt-0.5">Total Return</div>
        </div>
      </div>

      {/* Metrics strip */}
      <div className="mx-4 mb-3 bg-gray-50 rounded-lg px-4 py-3 flex items-center gap-2">
        <div className="flex items-center gap-6 flex-1 min-w-0 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-400">Called On</div>
            <div className="text-sm font-bold text-gray-800 mt-0.5">
              {formatDate(call.published_at ?? call.called_on) || '—'}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-400">Entry Price</div>
            <div className="text-sm font-bold text-gray-800 mt-0.5">
              {call.entry_price != null ? `$${parseFloat(call.entry_price).toFixed(2)}` : '—'}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-400">Peak Price</div>
            <div className="text-sm font-bold text-gray-800 mt-0.5">
              {call.peak_price != null ? `$${parseFloat(call.peak_price).toFixed(2)}` : '—'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-gray-500 flex-shrink-0 whitespace-nowrap">
          <Clock size={12} />
          {call.holding_period_days != null ? `Holding Period: ${call.holding_period_days} Days` : 'Holding Period: —'}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-4 pb-4">
        {call.source_url ? (
          <a
            href={cleanSourceUrl(call.source_url)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-blue-500 hover:text-blue-700 transition-colors"
          >
            <XIcon size={13} />
            X/Twitter Source Link
          </a>
        ) : (
          <span />
        )}
        <button
          onClick={() => setIsShareModalOpen(true)}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wide transition-colors bg-gray-100 text-gray-600 hover:bg-gray-200"
        >
          <Share2 size={12} />
          Share This Call
        </button>
      </div>

      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        title="Share This Call"
        shareUrl={predictionShareUrl ?? window.location.href}
        imageUrl={call.og_image_url || (call.id ? `${API_BASE_URL}/api/research/og/prediction/${call.id}/` : null)}
        downloadUrl={call.id ? `${API_BASE_URL}/api/research/og/prediction/${call.id}/?download=true` : undefined}
        shareText={`Check out this ${returnPct >= 0 ? '+' : ''}${returnPct.toFixed(1)}% call on ${call.symbol_ticker} on TraderBro!`}
      />
    </div>
  );
}
